import * as React from "react";
import {
  Box,
  Button,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField as MuiTextField,
  Typography,
} from "@mui/material";
import {
  Datagrid,
  FunctionField,
  List,
  TextField,
  TextInput,
  TopToolbar,
  useListContext,
} from "react-admin";
import type { AgentLead } from "../../models/AgentSession";
import { formatDate } from "../../utils/common";

/**
 * 格式化时间展示。
 */
const formatDateValue = (value: unknown): string => {
  if (typeof value !== "string" || !value) {
    return "-";
  }
  return formatDate(value);
};

/**
 * 货主线索列表筛选与操作栏。
 * 当前后端支持 city、company_name 过滤。
 */
const ListActions: React.FC = () => {
  const { filterValues, setFilters } = useListContext();
  const [city, setCity] = React.useState(String(filterValues.city ?? ""));
  const [companyName, setCompanyName] = React.useState(
    String(filterValues.company_name ?? "")
  );

  React.useEffect(() => {
    setCity(String(filterValues.city ?? ""));
    setCompanyName(String(filterValues.company_name ?? ""));
  }, [filterValues.city, filterValues.company_name]);

  const handleSearch = () => {
    setFilters(
      {
        ...filterValues,
        city: city.trim() || undefined,
        company_name: companyName.trim() || undefined,
      },
      undefined
    );
  };

  const handleReset = () => {
    setCity("");
    setCompanyName("");
    const next = { ...filterValues } as Record<string, unknown>;
    delete next.city;
    delete next.company_name;
    setFilters(next, undefined);
  };

  return (
    <TopToolbar sx={{ width: "100%", alignItems: "center", gap: 1, flexWrap: "wrap", px: 2, py: 1 }}>
      <MuiTextField
        size="small"
        label="城市"
        value={city}
        onChange={(event) => setCity(event.target.value)}
        sx={{ minWidth: 160 }}
      />
      <MuiTextField
        size="small"
        label="公司名称"
        value={companyName}
        onChange={(event) => setCompanyName(event.target.value)}
        sx={{ minWidth: 200 }}
      />
      <Button variant="contained" onClick={handleSearch}>
        查询
      </Button>
      <Button onClick={handleReset}>重置</Button>
    </TopToolbar>
  );
};

/**
 * 货主线索详情弹窗。
 */
const LeadDetailDialog: React.FC<{
  open: boolean;
  lead: AgentLead | null;
  onClose: () => void;
}> = ({ open, lead, onClose }) => {
  const [showRaw, setShowRaw] = React.useState(false);

  React.useEffect(() => {
    if (!open) {
      setShowRaw(false);
    }
  }, [open]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>货主线索详情</DialogTitle>
      <DialogContent dividers>
        {lead ? (
          <Stack spacing={1.5}>
            <Typography variant="body2">公司：{lead.company || "-"}</Typography>
            <Typography variant="body2">城市：{lead.city || "-"}</Typography>
            <Typography variant="body2">姓名：{lead.name || "-"}</Typography>
            <Typography variant="body2">电话：{lead.phone || "-"}</Typography>
            <Typography variant="body2">来源：{lead.source || "-"}</Typography>
            <Typography variant="body2">状态：{lead.status || "-"}</Typography>
            <Typography variant="body2">创建时间：{formatDateValue(lead.createdAt)}</Typography>
            <Typography variant="body2">更新时间：{formatDateValue(lead.updatedAt)}</Typography>
            <Box>
              <Button size="small" onClick={() => setShowRaw((previous) => !previous)}>
                {showRaw ? "收起原始数据" : "展开原始数据"}
              </Button>
              <Collapse in={showRaw}>
                <Box
                  component="pre"
                  sx={{
                    mt: 1,
                    p: 1.5,
                    bgcolor: "grey.100",
                    borderRadius: 1,
                    overflow: "auto",
                    maxHeight: 280,
                    fontSize: 12,
                  }}
                >
                  {JSON.stringify(lead.raw, null, 2)}
                </Box>
              </Collapse>
            </Box>
          </Stack>
        ) : (
          <Typography color="text.secondary">暂无数据</Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>关闭</Button>
      </DialogActions>
    </Dialog>
  );
};

/**
 * 货主线索表格。
 */
const AgentLeadsDatagrid: React.FC = () => {
  const [detailOpen, setDetailOpen] = React.useState(false);
  const [selectedLead, setSelectedLead] = React.useState<AgentLead | null>(null);

  const handleOpenDetail = (record: AgentLead) => {
    setSelectedLead(record);
    setDetailOpen(true);
  };

  return (
    <>
      <Datagrid
        rowClick={(_, __, record) => {
          handleOpenDetail(record as AgentLead);
          return false as const;
        }}
        bulkActionButtons={false}
      >
        <TextField source="company" label="公司" />
        <TextField source="city" label="城市" />
        <TextField source="name" label="姓名" />
        <TextField source="phone" label="电话" />
        <TextField source="source" label="来源" />
        <TextField source="status" label="状态" />
        <FunctionField
          label="创建时间"
          sortBy="createdAt"
          render={(record: AgentLead) => formatDateValue(record?.createdAt)}
        />
      </Datagrid>
      <LeadDetailDialog
        open={detailOpen}
        lead={selectedLead}
        onClose={() => {
          setDetailOpen(false);
          setSelectedLead(null);
        }}
      />
    </>
  );
};

/**
 * 智能体查找到的货主线索列表页。
 */
const AgentLeadsPage: React.FC = (props) => {
  return (
    <List
      {...props}
      title="货主线索"
      actions={<ListActions />}
      sort={{ field: "createdAt", order: "DESC" }}
      perPage={25}
      filters={[
        <TextInput key="city" source="city" label="城市" alwaysOn sx={{ display: "none" }} />,
        <TextInput
          key="company_name"
          source="company_name"
          label="公司名称"
          alwaysOn
          sx={{ display: "none" }}
        />,
      ]}
    >
      <AgentLeadsDatagrid />
    </List>
  );
};

export default AgentLeadsPage;
